import React, { useEffect } from 'react';

const TermsOfService = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="legal-page">
            <div className="legal-hero section-padding">
                <div className="container">
                    <h1 className="legal-title">Terms of Service</h1>
                    <p className="legal-subtitle">Last updated: December 2025</p>
                </div>
            </div>

            <div className="container">
                <div className="legal-content">
                    <section className="legal-section">
                        <h2>Agreement to Terms</h2>
                        <p>
                            These Terms of Service ("Terms") govern your access to and use of the Vintage Glass Works
                            website and services. By accessing our website or engaging our services, you agree to be
                            bound by these Terms. If you disagree with any part of these terms, you may not access
                            our website or use our services.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Services Description</h2>
                        <p>
                            Vintage Glass Works provides historic glass and door restoration services, including
                            but not limited to:
                        </p>
                        <ul>
                            <li>Leaded and stained glass restoration</li>
                            <li>Historic door restoration and reconstruction</li>
                            <li>Window preservation and conservation</li>
                            <li>Custom glass design and fabrication</li>
                            <li>Protective glazing installation</li>
                            <li>Consultation and assessment services</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>Estimates and Quotes</h2>
                        <p>
                            All estimates and quotes provided by Vintage Glass Works are valid for 30 days from
                            the date of issue unless otherwise specified in writing. Estimates are based on the
                            information provided and conditions observed at the time of assessment.
                        </p>
                        <p>
                            Final pricing may vary if:
                        </p>
                        <ul>
                            <li>Hidden damage is discovered during the restoration process</li>
                            <li>Project scope changes at the client's request</li>
                            <li>Material costs significantly increase</li>
                            <li>Additional work is required beyond the original scope</li>
                        </ul>
                        <p>
                            Any changes to the original estimate will be communicated and approved in writing
                            before additional work is performed.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Project Agreements</h2>
                        <p>
                            All restoration projects require a signed project agreement before work begins.
                            The agreement will outline:
                        </p>
                        <ul>
                            <li>Scope of work</li>
                            <li>Timeline and milestones</li>
                            <li>Payment schedule</li>
                            <li>Materials and methods to be used</li>
                            <li>Warranty terms</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>Payment Terms</h2>
                        <p>Payment terms are as follows unless otherwise agreed in writing:</p>
                        <ul>
                            <li><strong>Deposit:</strong> 50% of the estimated project cost is due upon signing the project agreement</li>
                            <li><strong>Progress Payment:</strong> For projects exceeding $10,000, additional progress payments may be required</li>
                            <li><strong>Final Payment:</strong> Remaining balance is due upon project completion and client approval</li>
                        </ul>
                        <p>
                            We accept payment by check, credit card, or bank transfer. A 3% processing fee applies
                            to credit card payments over $1,000.
                        </p>
                        <p>
                            Late payments may incur a 1.5% monthly interest charge. Work may be suspended on
                            accounts more than 30 days past due.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Cancellation Policy</h2>
                        <p>
                            If you need to cancel a project after signing the agreement:
                        </p>
                        <ul>
                            <li><strong>Before work begins:</strong> Full deposit refund minus a $250 administrative fee</li>
                            <li><strong>After work begins:</strong> Deposit is non-refundable; client is responsible for costs incurred to date</li>
                            <li><strong>Custom materials ordered:</strong> Client is responsible for non-refundable material costs</li>
                        </ul>
                        <p>
                            Cancellation requests must be submitted in writing via email or certified mail.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Warranty</h2>
                        <p>
                            Vintage Glass Works provides the following warranties on completed work:
                        </p>
                        <ul>
                            <li><strong>Workmanship:</strong> 5-year warranty against defects in workmanship</li>
                            <li><strong>Lead came and soldering:</strong> 5-year warranty under normal conditions</li>
                            <li><strong>Structural repairs:</strong> 3-year warranty on wood and frame repairs</li>
                        </ul>
                        <p>
                            The warranty does not cover:
                        </p>
                        <ul>
                            <li>Damage caused by accidents, vandalism, or natural disasters</li>
                            <li>Normal wear and aging of materials</li>
                            <li>Damage resulting from improper maintenance or unauthorized repairs</li>
                            <li>Pre-existing conditions not addressed in the original scope of work</li>
                        </ul>
                        <p>
                            Warranty claims must be submitted in writing within the warranty period. We will
                            assess the issue and, if covered, repair or correct the defect at no additional charge.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Client Responsibilities</h2>
                        <p>Clients agree to:</p>
                        <ul>
                            <li>Provide accurate information about the project and property</li>
                            <li>Ensure safe and reasonable access to the work area</li>
                            <li>Obtain any necessary permits or approvals (unless otherwise agreed)</li>
                            <li>Protect adjacent areas and belongings during the project</li>
                            <li>Make timely decisions when client input is required</li>
                            <li>Provide timely payment according to agreed terms</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>Limitation of Liability</h2>
                        <p>
                            To the fullest extent permitted by law, Vintage Glass Works shall not be liable for
                            any indirect, incidental, special, consequential, or punitive damages, including
                            without limitation, loss of profits, data, use, goodwill, or other intangible losses.
                        </p>
                        <p>
                            Our total liability for any claims arising from our services shall not exceed the
                            total amount paid by you for the specific service giving rise to the claim.
                        </p>
                        <p>
                            We are not responsible for:
                        </p>
                        <ul>
                            <li>Pre-existing structural issues not included in the project scope</li>
                            <li>Delays caused by factors outside our control (weather, material availability, etc.)</li>
                            <li>Damage to extremely fragile or deteriorated materials that cannot withstand restoration</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>Insurance</h2>
                        <p>
                            Vintage Glass Works maintains comprehensive general liability insurance and workers'
                            compensation coverage. Certificates of insurance are available upon request.
                        </p>
                        <p>
                            Clients are encouraged to maintain adequate homeowner's or property insurance during
                            restoration projects.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Intellectual Property</h2>
                        <p>
                            All content on this website, including text, images, logos, and designs, is the
                            property of Vintage Glass Works and is protected by copyright and trademark laws.
                            You may not reproduce, distribute, or create derivative works without our written permission.
                        </p>
                        <p>
                            We may photograph completed projects for our portfolio and marketing materials unless
                            the client requests otherwise in writing.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Website Use</h2>
                        <p>By using our website, you agree not to:</p>
                        <ul>
                            <li>Use the website for any unlawful purpose</li>
                            <li>Attempt to gain unauthorized access to any part of the website</li>
                            <li>Interfere with the proper functioning of the website</li>
                            <li>Submit false or misleading information through our contact forms</li>
                            <li>Use automated systems to access the website without permission</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>Dispute Resolution</h2>
                        <p>
                            Any disputes arising from these Terms or our services shall first be addressed through
                            good-faith negotiation. If a resolution cannot be reached, disputes shall be resolved
                            through binding arbitration in accordance with the rules of the American Arbitration
                            Association, conducted in the State of Maine.
                        </p>
                        <p>
                            These Terms shall be governed by and construed in accordance with the laws of the
                            State of Maine, without regard to conflict of law principles.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Severability</h2>
                        <p>
                            If any provision of these Terms is found to be unenforceable or invalid, that provision
                            shall be limited or eliminated to the minimum extent necessary, and the remaining
                            provisions shall remain in full force and effect.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Changes to Terms</h2>
                        <p>
                            We reserve the right to modify these Terms at any time. Changes will be effective
                            immediately upon posting to the website. Your continued use of our website or services
                            after changes are posted constitutes acceptance of the modified Terms.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>Contact Us</h2>
                        <p>If you have questions about these Terms of Service, please contact us:</p>
                        <div className="legal-contact">
                            <p><strong>Vintage Glass Works</strong></p>
                            <p>Email: info@vintageglassworks.com</p>
                            <p>Phone: (207) 588-7000</p>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TermsOfService;